import { useMemo } from 'react';

import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import type { Estate } from '@/features/estates-property/api/estate';
import type { Project } from '@/features/projects/api/projects';
import type { QueryConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { api, extractData } from '@/lib/treaty';
import { groupBy } from '@/utils/groupBy';
import { uniqBy } from '@/utils/uniqBy';

export type MediaQueryOptions = {
  queryConfig?: QueryConfig<typeof getMedia>;
  id: Estate['id'];
};

export type MediaByIdQueryOptions = {
  queryConfig?: QueryConfig<typeof getMedia>;
  id: Estate['id'];
  mediaId: string;
};

export const updateMediaImageName = (id: Estate['id'], imageId: string, name: string) => {
  return extractData(api.estates({ id }).images({ imageId: imageId }).patch({ name }));
};

export const getMedia = (id: Estate['id']) => {
  return extractData(api.estates({ id }).media.get());
};

export const getMediaById = (id: Estate['id'], mediaId: string) => {
  return extractData(api.estates({ id }).media({ mediaId }).get());
};

export const getMediaOptions = (id: Estate['id']) => ({
  queryKey: ['media', id],
  queryFn: () => getMedia(id),
});

export const getMediaByIdOptions = (id: Estate['id'], mediaId: string) => ({
  queryKey: ['media', id, mediaId],
  queryFn: () => getMediaById(id, mediaId),
});

export const getMediaQueryOptions = (id: Estate['id']) => {
  return queryOptions(getMediaOptions(id));
};

export const getMediaByIdQueryOptions = (id: Estate['id'], mediaId: string) => {
  return queryOptions(getMediaByIdOptions(id, mediaId));
};

export const ensureMediaQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getMediaQueryOptions(params.estateId));
};

export const ensureMediaByIdQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getMediaByIdQueryOptions(params.estateId, params.mediaId));
};

export const useMedia = ({ queryConfig, id }: MediaQueryOptions) => {
  return useSuspenseQuery({ ...queryConfig, ...getMediaQueryOptions(id) });
};

export const useMediaGetById = ({ queryConfig, id, mediaId }: MediaByIdQueryOptions) => {
  return useSuspenseQuery({ ...queryConfig, ...getMediaByIdQueryOptions(id, mediaId) });
};

export type MediaResponseType = NonNullable<
  Awaited<ReturnType<ReturnType<typeof api.estates>['media']['get']>>['data']
>;

export type DigitalTwinsType = MediaResponseType['digitalTwins'];
export type ImagesType = MediaResponseType['images'];
export type SphericalImagesType = MediaResponseType['sphericalImages'];
export type FloorPlans = MediaResponseType['floorLayouts'];
export type FocusPointsType = MediaResponseType['pois'];

// --------- project media ----------

export const getMediaProjectById = (id: Project['id'], mediaId: string) => {
  return extractData(api.projects({ id }).media({ mediaId }).get());
};

export const getMediaProjectByIdOptions = (id: Project['id'], mediaId: string) => ({
  queryKey: ['media', id, mediaId],
  queryFn: () => getMediaProjectById(id, mediaId),
});

export const ensureMediaProjectByIdQueryData = ({
  context: { queryClient },
  params,
}: LoaderProps) => {
  return queryClient.ensureQueryData(getMediaProjectByIdOptions(params.projectId, params.mediaId));
};

export const getMediaProjectByIdQueryOptions = (id: Project['id'], mediaId: string) => {
  return queryOptions(getMediaProjectByIdOptions(id, mediaId));
};

export const useMediaProjectGetById = ({
  queryConfig,
  id,
  mediaId,
}: {
  queryConfig?: QueryConfig<typeof getMedia>;
  id: Project['id'];
  mediaId: string;
}) => {
  return useSuspenseQuery({ ...queryConfig, ...getMediaProjectByIdQueryOptions(id, mediaId) });
};

export const updateMediaProjectImageName = (id: Project['id'], imageId: string, name: string) => {
  return extractData(api.projects({ id }).images({ imageId: imageId }).patch({ name }));
};

export type ProjectMediaQueryOptions = {
  queryConfig?: QueryConfig<typeof getProjectMedia>;
  id?: Project['id'] | null;
};

export type ProjectMediaResponseType = NonNullable<
  Awaited<ReturnType<ReturnType<typeof api.projects>['media']['get']>>['data']
>;

//updatedAt, deletedAt, fileTypets
export type ProjectImageType = ProjectMediaResponseType['images'][number];
export type ProjectLayoutType = ProjectMediaResponseType['layouts'][number];

export const getProjectMedia = (id: Project['id']) => {
  return extractData(api.projects({ id }).media.get());
};

export const getProjectMediaOptions = (id?: Project['id'] | null) => ({
  queryKey: ['media', id],
  queryFn: () => {
    if (id) {
      return getProjectMedia(id);
    } else {
      return Promise.resolve({
        images: [],
        documents: [],
        sphericalImages: [],
        layouts: [],
      });
    }
  },
});

export const getProjectMediaQueryOptions = (id?: Project['id'] | null) => {
  return queryOptions(getProjectMediaOptions(id));
};

export const ensureProjectMediaQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getProjectMediaQueryOptions(params.projectId));
};

export const useProjectMedia = ({ queryConfig, id }: ProjectMediaQueryOptions) => {
  const { data: projectMedia, isPending } = useSuspenseQuery({
    ...queryConfig,
    ...getProjectMediaQueryOptions(id),
  });
  const data = useMemo(() => {
    if (projectMedia) {
      const groupRelations = ['images', 'layouts'] as const;
      // group the relation to create an object like the following:
      // { images: { "2024-11-23": [], "2024-11-22": []} }
      const result = groupRelations.reduce<
        Record<
          (typeof groupRelations)[number],
          Record<string, ProjectImageType[] | ProjectLayoutType[]>
        >
      >(
        (acc, relation) => {
          acc.images;
          acc[relation] = groupBy(
            (item) => dayjs(item.createdAt).format('YYYY-MM-DD'),
            // Project images are not showing any images from Estates => filter out media without estateId
            uniqBy(
              (projectMedia[relation] || []).filter((media) => !media.estateId),
              'id'
            )
          );
          return acc;
        },
        { images: {}, layouts: {} }
      );
      return result;
    } else {
      return {
        images: {},
        layouts: {},
      };
    }
  }, [projectMedia]);
  return { data, isPending };
};
